/* ============================================================
   PDP Lightbox
   - Cursor follower (+ icon) on gallery hover
   - Click image (grid / main / mobile slide) → open lightbox
   - Prev/next arrows, counter, keyboard nav
   - Click image inside lightbox → zoom toggle
   - Mobile: swipe to navigate, pinch zoom via CSS
   ============================================================ */

class PdpLightbox {
  constructor(root) {
    this.root = root;
    this.section = root.dataset.section || '';
    this.slides = Array.from(root.querySelectorAll('.pdp-lightbox__slide'));
    this.prevBtn = root.querySelector('.pdp-lightbox__nav--prev');
    this.nextBtn = root.querySelector('.pdp-lightbox__nav--next');
    this.closeBtn = root.querySelector('.pdp-lightbox__close');
    this.counterCurrent = root.querySelector('.pdp-lightbox__current');
    this.counterTotal = root.querySelector('.pdp-lightbox__total');
    this.currentIndex = 0;

    if (!this.slides.length) return;

    if (this.counterTotal) this.counterTotal.textContent = this.slides.length;

    this.prevBtn && this.prevBtn.addEventListener('click', () => this.go(-1));
    this.nextBtn && this.nextBtn.addEventListener('click', () => this.go(1));
    this.closeBtn && this.closeBtn.addEventListener('click', () => this.close());

    const isTouch = window.matchMedia('(hover: none)').matches;
    this.slides.forEach((slide) => {
      const media = slide.querySelector('.pdp-lightbox__media');
      if (!media) return;

      if (isTouch) {
        // Mobile: double-tap to toggle zoom (native image viewer feel)
        let lastTap = 0;
        let lastX = 0;
        let lastY = 0;
        media.addEventListener('touchend', (e) => {
          if (e.touches.length > 0) return; // ignore multi-touch (pinch) endings
          const t = e.changedTouches[0];
          const now = Date.now();
          const dt = now - lastTap;
          const dist = Math.hypot(t.clientX - lastX, t.clientY - lastY);
          if (dt < 350 && dt > 0 && dist < 30) {
            e.preventDefault();
            this.handleZoom({ clientX: t.clientX, clientY: t.clientY }, media);
            lastTap = 0;
          } else {
            lastTap = now;
            lastX = t.clientX;
            lastY = t.clientY;
          }
        });
      } else {
        // Desktop: single click toggles zoom
        media.addEventListener('click', (e) => this.handleZoom(e, media));
      }
    });

    this.onKey = this.onKey.bind(this);
    this.bindTouch();
    this.bindOpeners();
  }

  bindOpeners() {
    const sectionEl = document.getElementById('pdp-' + this.section);
    if (!sectionEl) return;

    const openables = sectionEl.querySelectorAll(
      '.pdp-grid-item, .pdp-main-img, .pdp-slide'
    );
    openables.forEach((el) => {
      el.addEventListener('click', (e) => {
        const idx = parseInt(el.dataset.index, 10);
        const safeIdx = Number.isFinite(idx) ? idx : this.indexOfChild(el);
        this.open(safeIdx);
      });
    });
  }

  indexOfChild(el) {
    const siblings = Array.from(el.parentElement?.children || []);
    return siblings.indexOf(el);
  }

  open(index) {
    // Compensate for scrollbar disappearing when body gets overflow:hidden
    this.scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Stop Lenis (theme's smooth-scroll library) so it doesn't animate during
    // modal lifecycle. Without this, close() could trigger a 2-second smooth
    // glide back to the saved position.
    if (window.lenis && typeof window.lenis.stop === 'function') {
      window.lenis.stop();
    }

    this.root.removeAttribute('hidden');

    // Snap slides into position WITHOUT transition (avoids flash on initial open
    // when user opens at non-first image — slides shouldn't fly in from edges)
    this.root.classList.add('lightbox-no-transition');
    this.setActive(index || 0);

    // Force reflow so the next class change triggers a transition
    void this.root.offsetWidth;
    this.root.classList.remove('lightbox-no-transition');

    // is-opening triggers the one-time scale-in for the first image only.
    // Removed after the animation finishes so subsequent slide navigations
    // don't re-trigger the scale (which caused white flash at top/bottom).
    this.root.classList.add('is-opening');
    setTimeout(() => this.root.classList.remove('is-opening'), 450);

    requestAnimationFrame(() => {
      this.root.classList.add('is-open');
    });

    // Lock page scroll via overflow:hidden — browser keeps the scroll position
    // exactly where it is. No position:fixed trick, no scrollY tracking, no
    // restore needed on close. paddingRight prevents horizontal layout shift
    // when the native scrollbar disappears.
    document.body.classList.add('pdp-lightbox-open');
    if (this.scrollbarWidth > 0) {
      document.body.style.paddingRight = this.scrollbarWidth + 'px';
    }
    document.addEventListener('keydown', this.onKey);
  }

  close() {
    this.root.classList.remove('is-open');

    // Unlock scroll. Browser-preserved scroll position takes over — page
    // stays exactly where the user opened the modal from. No scrollTo,
    // no jumps, no clamping issues.
    document.body.classList.remove('pdp-lightbox-open');
    document.body.style.paddingRight = '';

    // Resume Lenis. Since we didn't move scroll position, Lenis has nothing
    // to animate towards — it just resumes from the current scroll.
    if (window.lenis && typeof window.lenis.start === 'function') {
      window.lenis.start();
    }

    document.removeEventListener('keydown', this.onKey);
    this.slides.forEach((s) => {
      const m = s.querySelector('.pdp-lightbox__media');
      m && this.resetZoom(m);
    });

    // Re-add `hidden` after the fade-out transition finishes (matches CSS 0.28s)
    setTimeout(() => {
      if (!this.isOpen()) {
        this.root.setAttribute('hidden', '');
      }
    }, 300);
  }

  isOpen() {
    return this.root.classList.contains('is-open');
  }

  onKey(e) {
    if (e.key === 'ArrowLeft') this.go(-1);
    else if (e.key === 'ArrowRight') this.go(1);
    else if (e.key === 'Escape') this.close();
  }

  setActive(index) {
    // Clamp to bounds — no looping (user explicitly doesn't want wrap-around)
    if (index < 0) index = 0;
    if (index >= this.slides.length) index = this.slides.length - 1;
    if (index === this.currentIndex && this.slides[index]?.classList.contains('is-active')) {
      return;
    }
    this.currentIndex = index;

    this.slides.forEach((s, i) => {
      s.classList.remove('is-active', 'is-prev', 'is-next');
      if (i === index) {
        s.classList.add('is-active');
      } else if (i < index) {
        s.classList.add('is-prev');
      } else {
        s.classList.add('is-next');
      }
      if (i !== index) {
        const m = s.querySelector('.pdp-lightbox__media');
        m && this.resetZoom(m);
      }
    });

    if (this.counterCurrent) this.counterCurrent.textContent = index + 1;
    this.updateNavState();
  }

  updateNavState() {
    if (this.prevBtn) {
      this.prevBtn.toggleAttribute('disabled', this.currentIndex === 0);
    }
    if (this.nextBtn) {
      this.nextBtn.toggleAttribute('disabled', this.currentIndex === this.slides.length - 1);
    }
  }

  go(delta) {
    this.setActive(this.currentIndex + delta);
  }

  handleZoom(e, media) {
    if (media.classList.contains('is-zoomed')) {
      this.resetZoom(media);
      return;
    }
    const img = media.querySelector('img');
    const target = img || media;
    const rect = target.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    media.style.setProperty('--zoom-x', x + '%');
    media.style.setProperty('--zoom-y', y + '%');
    if (img) {
      img.style.setProperty('--zoom-x', x + '%');
      img.style.setProperty('--zoom-y', y + '%');
    }
    media.classList.add('is-zoomed');
  }

  resetZoom(media) {
    // Only remove the zoom class — keep --zoom-x / --zoom-y so the
    // transform-origin stays at the same point during the scale-down
    // animation. Otherwise the origin snaps back to 50% 50% instantly
    // and the image appears to jump.
    media.classList.remove('is-zoomed');
  }

  bindTouch() {
    let startX = 0;
    let startY = 0;
    let moved = false;

    this.root.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1) return;
        const activeMedia = this.slides[this.currentIndex]?.querySelector(
          '.pdp-lightbox__media'
        );
        if (activeMedia?.classList.contains('is-zoomed')) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        moved = false;
      },
      { passive: true }
    );

    this.root.addEventListener(
      'touchmove',
      () => {
        moved = true;
      },
      { passive: true }
    );

    this.root.addEventListener(
      'touchend',
      (e) => {
        if (!moved) return;
        const endX = e.changedTouches[0].clientX;
        const endY = e.changedTouches[0].clientY;
        const dx = endX - startX;
        const dy = endY - startY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          this.go(dx < 0 ? 1 : -1);
        }
      },
      { passive: true }
    );
  }
}

/* ============================================================
   Cursor follower (+ icon)
   ============================================================ */
class PdpGalleryCursor {
  constructor(gallery) {
    this.gallery = gallery;
    this.cursor = gallery.querySelector('.pdp-gallery__cursor');
    this.items = gallery.querySelectorAll(
      '.pdp-grid-item, .pdp-main-img, .pdp-slide'
    );

    if (!this.cursor || !this.items.length) return;
    // Skip on mobile entirely — width-based check matches the rest of the
    // theme's mobile detection and works reliably in DevTools emulation too.
    if (window.matchMedia('(max-width: 767px)').matches) return;

    // CRITICAL: move cursor out of the gallery and into <body>.
    // The sticky scroll logic applies a `transform` to the gallery in
    // thumbnail mode. Any descendant with `position: fixed` is then
    // positioned relative to the transformed ancestor (CSS containing-block
    // rule) instead of the viewport, which makes the cursor drift after
    // scroll. Moving it to <body> ensures the viewport is always its
    // containing block, so e.clientX/Y track correctly regardless of any
    // transforms further up the tree.
    document.body.appendChild(this.cursor);

    this.handleMove = this.handleMove.bind(this);
    this.handleEnter = this.handleEnter.bind(this);
    this.handleLeave = this.handleLeave.bind(this);

    this.items.forEach((item) => {
      item.addEventListener('mouseenter', this.handleEnter);
      item.addEventListener('mousemove', this.handleMove);
      item.addEventListener('mouseleave', this.handleLeave);
    });
  }

  handleEnter() {
    this.cursor.classList.add('is-active');
  }

  handleMove(e) {
    this.cursor.style.left = e.clientX + 'px';
    this.cursor.style.top = e.clientY + 'px';
  }

  handleLeave() {
    this.cursor.classList.remove('is-active');
  }
}

/* ============================================================
   Init
   ============================================================ */
function initPdpLightbox() {
  document.querySelectorAll('.pdp-lightbox').forEach((root) => {
    if (root.dataset.lightboxInit) return;
    root.dataset.lightboxInit = '1';
    new PdpLightbox(root);
  });

  document.querySelectorAll('.pdp-gallery').forEach((gallery) => {
    if (gallery.dataset.cursorInit) return;
    gallery.dataset.cursorInit = '1';
    new PdpGalleryCursor(gallery);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPdpLightbox);
} else {
  initPdpLightbox();
}
